import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  MYNTRA_IMPORT,
  MYNTRA_ORDERS_LIST,
  MYNTRA_RETURNS,
  MYNTRA_PAYMENTS,
  MYNTRA_PRODUCT_REPORT,
  MYNTRA_STATISTICS,
  MYNTRA_FINANCE_DASHBOARD,
  MYNTRA_MARKETING_EXPENSES,
  MYNTRA_RETURN_REASON_ANALYSIS,
} from "app/routes/constant";
import { userType } from "app/helpers/userType";

const MYNTRA_PINK = "#FF3F6C";

const menuItems = [
  {
    title: "Import",
    icon: "material-symbols:deployed-code-update",
    route: MYNTRA_IMPORT,
    color: MYNTRA_PINK,
    description: "Upload order sheet (CSV/Excel) and view import history",
    permission: "myntra-import",
  },
  {
    title: "Orders List",
    icon: "material-symbols:list-alt",
    route: MYNTRA_ORDERS_LIST,
    color: "#9C27B0",
    description: "View and manage all Myntra orders",
    permission: "myntra-order-list",
  },
  {
    title: "Return List",
    icon: "material-symbols:assignment-return",
    route: MYNTRA_RETURNS,
    color: "#FF9800",
    description: "View and manage returned orders",
    permission: "myntra-return-list",
  },
  {
    title: "Payment",
    icon: "material-symbols:currency-rupee",
    route: MYNTRA_PAYMENTS,
    color: "#3F51B5",
    description: "View payment received and settlement status",
    permission: "myntra-payment-list",
  },
  {
    title: "Product Report",
    icon: "material-symbols:analytics",
    route: MYNTRA_PRODUCT_REPORT,
    color: "#009688",
    description: "Product-wise orders, returns, cost and P&L report",
    permission: "myntra-product-report",
  },
  {
    title: "Statistics",
    icon: "material-symbols:bar-chart",
    route: MYNTRA_STATISTICS,
    color: "#F44336",
    description: "Status-wise product count and summary",
    permission: "myntra-statistics",
  },
  {
    title: "Finance Dashboard",
    icon: "material-symbols:dashboard",
    route: MYNTRA_FINANCE_DASHBOARD,
    color: "#4CAF50",
    description: "Total profit/loss, sales, settled & pending amounts",
    permission: "myntra-finance-dashboard",
  },
  {
    title: "Marketing And Expenses List",
    icon: "material-symbols:attach-money",
    route: MYNTRA_MARKETING_EXPENSES,
    color: "#180d0d",
    description: "Marketing And Expenses manages and tracks business costs.",
    permission: "myntra-marketing-expenses",
  },
  {
    title: "Return Reason Analysis",
    icon: "material-symbols:analytics",
    route: MYNTRA_RETURN_REASON_ANALYSIS,
    color: "#E91E63",
    description: "Upload returns CSV and analyse return reasons by SKU",
    permission: "myntra-return-list",
  },
];

const MyntraMenu = () => {
  const { isMA } = userType();
  const navigate = useNavigate();
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    importPermission: false,
    orderPermission: false,
    returnPermission: false,
    paymentPermission: false,
    productPermission: false,
    statisticsPermission: false,
    dashboardPermission: false,
    marketingPermission: false,
  });

  const hasMenuPermission = (permissionName) => {
    const permissionMap = {
      "myntra-import": permission.importPermission,
      "myntra-order-list": permission.orderPermission,
      "myntra-return-list": permission.returnPermission,
      "myntra-payment-list": permission.paymentPermission,
      "myntra-product-report": permission.productPermission,
      "myntra-statistics": permission.statisticsPermission,
      "myntra-finance-dashboard": permission.dashboardPermission,
      "myntra-marketing-expenses": permission.marketingPermission,
    };

    return Boolean(permissionMap[permissionName]);
  };

  const getPermission = () => {
    if (isMA) {
      setPermission({
        importPermission: true,
        orderPermission: true,
        returnPermission: true,
        paymentPermission: true,
        productPermission: true,
        statisticsPermission: true,
        dashboardPermission: true,
        marketingPermission: true,
      });
    } else {
      let permissionValue = {
        importPermission: false,
        orderPermission: false,
        returnPermission: false,
        paymentPermission: false,
        productPermission: false,
        statisticsPermission: false,
        dashboardPermission: false,
        marketingPermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "myntra-import") {
            permissionValue.importPermission = true;
          }
          if (num?.name === "myntra-order-list") {
            permissionValue.orderPermission = true;
          }
          if (num?.name === "myntra-return-list") {
            permissionValue.returnPermission = true;
          }
          if (num?.name === "myntra-payment-list") {
            permissionValue.paymentPermission = true;
          }
          if (num?.name === "myntra-product-report") {
            permissionValue.productPermission = true;
          }
          if (num?.name === "myntra-statistics") {
            permissionValue.statisticsPermission = true;
          }
          if (num?.name === "myntra-finance-dashboard") {
            permissionValue.dashboardPermission = true;
          }
          if (num?.name === "myntra-marketing-expenses") {
            permissionValue.marketingPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 3, fontWeight: "bold" }}>
        Myntra Management
      </Typography>

      <Grid container spacing={3}>
        {menuItems
          .filter((item) => hasMenuPermission(item.permission))
          .map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  },
                  border: `2px solid ${item.color}20`,
                  borderRadius: 2,
                }}
                onClick={() => navigate(item.route)}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: `${item.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px auto",
                      border: `3px solid ${item.color}30`,
                    }}
                  >
                    <Icon
                      icon={item.icon}
                      style={{ fontSize: "40px", color: item.color }}
                    />
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 1, color: item.color }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {item.description}
                  </Typography>

                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: item.color,
                      "&:hover": { backgroundColor: item.color, opacity: 0.8 },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "bold",
                    }}
                    startIcon={<Icon icon="material-symbols:arrow-forward" />}
                  >
                    Open {item.title}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

export default MyntraMenu;
